/**
 * Who saved the draft: "you", a teammate's name or email, or an AI agent by
 * its token's label ("Claude Code (AI agent)"). The config row only records
 * the partner user (an agent saves as the person who created its token), so
 * the save's audit entry (`config.save` on `widget_configs/<key>/<version>`)
 * is what tells an agent apart. Owners and admins — the only roles that edit
 * settings — may read both the audit log and the team; a failed read falls
 * back to the member, then to nothing.
 */
import { useSession } from '../auth/session';
import { useQuery } from '../ui/hooks';

export function useDraftAuthor(siteKey: string, version: number | null, updatedBy: number | null, enabled: boolean): string | null {
  const { api, whoami } = useSession();
  const on = enabled && version !== null && version > 0;
  const audit = useQuery(() => api.audit({ action: 'config.save' }), [siteKey, version], { enabled: on });
  const members = useQuery(() => api.listMembers(), [], { enabled: on });
  if (!on) return null;
  const entry = audit.data?.items.find((e) => e.resource === `widget_configs/${siteKey}/${version}`);
  if (entry?.actor_agent_token_id) return `${entry.actor_agent_token_label ?? 'an AI agent'} (AI agent)`;
  const userId = entry?.actor_user_id ?? updatedBy;
  if (userId === null || userId === undefined) return null;
  if (whoami && userId === whoami.partner_user_id) return 'you';
  const m = members.data?.items.find((x) => x.user_id === userId);
  return m ? (m.name ?? m.email) : null;
}
